import { useContext } from 'react';
import { Handle, Position } from 'reactflow';
import { NODE_COLORS } from './nodeUtils';
import { FlowStatsContext } from '../../FlowStatsContext';
import NodeHeader from './NodeHeader';
import NodeHandles from './NodeHandles';

import TextNode       from './nodes/TextNode';
import DelayNode      from './nodes/DelayNode';
import SmartDelayNode from './nodes/SmartDelayNode';
import TypingNode     from './nodes/TypingNode';
import ImageNode      from './nodes/media/ImageNode';
import VideoNode      from './nodes/media/VideoNode';
import AudioNode      from './nodes/media/AudioNode';
import InputNode      from './nodes/InputNode';
import RandomizerNode from './nodes/RandomizerNode';
import TriggerNode    from './nodes/TriggerNode';
import PaymentNode    from './nodes/PaymentNode';
import CardPaymentNode from './nodes/CardPaymentNode';
import SpeiPaymentNode from './nodes/SpeiPaymentNode';
import EntregavelNode from './nodes/EntregavelNode';
import LocationNode   from './nodes/LocationNode';
import ButtonsNode    from './nodes/ButtonsNode';
import ComposedNode   from './nodes/ComposedNode';
import GotoNode       from './nodes/GotoNode';
import OrderBumpNode  from './nodes/OrderBumpNode';

const NODE_CONTENT_MAP = {
    text:        TextNode,
    delay:       DelayNode,
    smart_delay: SmartDelayNode,
    typing:      TypingNode,
    image:       ImageNode,
    video:       VideoNode,
    audio:       AudioNode,
    input:       InputNode,
    randomizer:  RandomizerNode,
    trigger:     TriggerNode,
    payment:     PaymentNode,
    card_payment: CardPaymentNode,
    spei_payment: SpeiPaymentNode,
    entregavel:  EntregavelNode,
    location:    LocationNode,
    buttons:     ButtonsNode,
    composed:    ComposedNode,
    goto:        GotoNode,
    order_bump:  OrderBumpNode,
};

export default function FlowNodeComponent({ id, data, selected }) {
    const nodeType = data?.nodeType || 'text';
    const isStart = Boolean(data?.isStart || nodeType === 'start');
    const botId = data?.botId || '';

    const { enabled: statsEnabled, stats, starts } = useContext(FlowStatsContext);
    const reachCount = isStart ? (starts ?? 0) : (stats?.[id] ?? 0);

    const palette = NODE_COLORS[nodeType] || { border: 'border-white/10', badgeBg: 'bg-sky-500', cardBg: 'bg-black/70' };
    const baseClasses = `relative rounded-xl border-l-2 px-3 py-2 text-xs shadow-lg text-gray-100 bg-black ${
        nodeType === 'trigger' ? 'min-w-[260px] max-w-[380px]' : 'min-w-[200px] max-w-[320px]'
    }`;

    const NodeContent = NODE_CONTENT_MAP[nodeType];

    return (
        <div className={`${baseClasses} ${palette.border} ${palette.cardBg}`}>
            {statsEnabled && (
                <div
                    className="absolute -top-2 -right-2 z-10 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-md"
                    style={{ backgroundColor: 'var(--purple, #8b5cf6)' }}
                    title={`${reachCount} pessoa(s) chegaram a este nó`}
                >
                    <i className="fas fa-eye text-[9px]" />
                    <span className="tabular-nums">{reachCount}</span>
                </div>
            )}

            {!isStart && (
                <Handle type="target" position={Position.Top} className="w-2 h-2 bg-emerald-400" />
            )}

            <NodeHeader id={id} data={data} nodeType={nodeType} isStart={isStart} palette={palette} />

            {!isStart && NodeContent && (
                <NodeContent id={id} data={data} botId={botId} selected={selected} />
            )}

            <div className="mt-2 flex items-center text-[10px] text-gray-400">
                {data?.hasMedia && !['image', 'video', 'audio'].includes(nodeType) && (
                    <><i className="fas fa-photo-video text-emerald-400 mr-1" /><span>Mídia anexada</span></>
                )}
            </div>

            <NodeHandles nodeType={nodeType} isStart={isStart} data={data} />
        </div>
    );
}
